import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";
import { formatDistanceToNow } from "date-fns";
import "../../../css/rf.css";
import FlowEditor from "./FlowEditor";
import FlowApprovals from "./FlowApprovals";

const errMsg = (e) => e?.response?.data?.message || e.message || "Something went wrong";

const statusOf = (f) => {
  if (f.status === "paused") return ["", "Paused"];
  if (f.issues?.length || f.lastError) return ["amber", "Needs attention"];
  return ["green", "Active"];
};

const FlowsPage = () => {
  const [tab, setTab] = useState("flows");
  const [flows, setFlows] = useState([]);
  const [channels, setChannels] = useState([]);
  const [ctx, setCtx] = useState({});
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState("");
  const [editor, setEditor] = useState({ show: false, flow: null });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [fl, ch, ss, ai] = await Promise.all([
        axiosInstance.get("flows"),
        axiosInstance.get("channels").catch(() => ({ data: {} })),
        axiosInstance.get("channels/session-status").catch(() => ({ data: {} })),
        axiosInstance.get("autopilot/ai-settings").catch(() => ({ data: {} })),
      ]);
      setFlows(fl.data.data || []);
      setChannels(ch.data.data || []);
      setCtx({ connected: ss.data.status === "connected", phone: ss.data.phone, hasAIKey: ai.data?.data ? !!ai.data.data.hasKey : undefined });
      setBlocked("");
    } catch (e) {
      if (e?.response?.status === 403) setBlocked(errMsg(e)); else swal("Error", errMsg(e), "error");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const waiting = flows.reduce((a, f) => a + (f.waitingApproval || 0), 0);

  const run = async (f, fn, okTitle) => {
    setBusy(f._id);
    try { const r = await fn(); if (okTitle) swal(okTitle, r?.data?.message || "", "success"); await load(); }
    catch (e) { swal("Could not do that", errMsg(e), "error"); }
    setBusy("");
  };
  const toggle = (f) => run(f, () => axiosInstance.post(`flows/${f._id}/status`, { status: f.status === "active" ? "paused" : "active" }));
  const runNow = (f) => run(f, () => axiosInstance.post(`flows/${f._id}/run`), "Checked");
  const switchToAuto = (f) => run(f, () => axiosInstance.put(`flows/${f._id}`, { nodes: f.nodes.map((n) => (n.type === "approval" ? { ...n, config: { ...n.config, mode: "auto" } } : n)) }), "Now publishing on its own");
  const remove = async (f) => {
    const ok = await swal({ title: `Delete "${f.name}"?`, text: "Its scheduled posts will be cancelled.", icon: "warning", buttons: true, dangerMode: true });
    if (ok) run(f, () => axiosInstance.delete(`flows/${f._id}`));
  };

  if (loading) return <div className="rf text-center py-5"><Spinner animation="border" variant="success" /></div>;

  if (blocked) return (
    <div className="rf"><div className="rf-card rf-empty">
      <div className="ico">🔒</div><h5>Flows are not available</h5><p>{blocked}</p>
      <Link to="/packs" className="btn-rf d-inline-block">Upgrade plan</Link>
    </div></div>
  );

  return (
    <div className="rf">
      <div className="rf-head">
        <div className="rf-head-icon">🔀</div>
        <div><h3>Flows</h3><p>Automate your WhatsApp channels: pick a source, let AI write, approve, and post on a schedule.</p></div>
        <div className="rf-spacer" />
        <button className="btn-rf" onClick={() => setEditor({ show: true, flow: null })}>+ Create flow</button>
      </div>

      <div className="rf-tabs">
        <button className={tab === "flows" ? "on" : ""} onClick={() => setTab("flows")}>Flows</button>
        <button className={tab === "approvals" ? "on" : ""} onClick={() => setTab("approvals")}>Approvals{waiting > 0 && <span className="rf-badge">{waiting}</span>}</button>
      </div>

      {ctx.hasAIKey === false && (
        <div className="rf-alert amber d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
          <span>No AI key is connected yet, so flows cannot write posts.</span><div className="rf-spacer" />
          <Link to="/flows-ai" className="btn-rf-o">Add AI key</Link>
        </div>
      )}

      {tab === "approvals" ? <FlowApprovals onChanged={load} /> : flows.length === 0 ? (
        <div className="rf-card rf-empty">
          <div className="ico">🔀</div><h5>No flows yet</h5>
          <p>Create your first flow: for example, 5 posts a day from a blog's RSS feed, rewritten by AI, approved by you.</p>
          <button className="btn-rf" onClick={() => setEditor({ show: true, flow: null })}>Create your first flow</button>
        </div>
      ) : flows.map((f) => {
        const [cls, label] = statusOf(f);
        const t = f.totals || {};
        return (
          <div className="rf-card" key={f._id}>
            <div className="d-flex flex-wrap align-items-start" style={{ gap: 10 }}>
              <div>
                <span className="rf-flow-title">{f.name}</span>{" "}<span className={`rf-chip ${cls}`}>{label}</span>
                {f.waitingApproval > 0 && <span className="rf-chip amber ml-2">{f.waitingApproval} waiting</span>}
                <div className="rf-meta mt-1">
                  → 📢 {f.channelName || "no channel"}{f.postsAt?.length ? ` · posts at ${f.postsAt.slice(0, 8).join(", ")}${f.postsAt.length > 8 ? "…" : ""}` : ""}
                  {f.lastRunAt ? ` · checked ${formatDistanceToNow(new Date(f.lastRunAt))} ago` : ""}
                </div>
                <div className="rf-meta">{t.generated || 0} written · {t.posted || 0} posted · {t.rejected || 0} rejected · {t.skipped || 0} skipped · {t.failed || 0} failed</div>
              </div>
            </div>
            {f.issues?.length > 0 && <div className="rf-alert red mt-2 mb-0">Fix this flow: {f.issues[0].message}</div>}
            {f.status === "paused" && f.pauseReason && <div className="rf-alert amber mt-2 mb-0">{f.pauseReason}</div>}
            {f.status === "active" && f.lastError && <div className="rf-alert amber mt-2 mb-0">{f.lastError}</div>}
            {f.status === "active" && !f.lastError && f.lastNote && <div className="rf-help">{f.lastNote}</div>}
            {f.suggestAuto && (
              <div className="rf-alert amber mt-2 mb-0 d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                <span>This flow is doing great ({f.approvedClean} clean approvals). Let it publish on its own?</span><div className="rf-spacer" />
                <button className="btn-rf" onClick={() => switchToAuto(f)}>Publish on its own</button>
              </div>
            )}
            <div className="d-flex flex-wrap mt-3" style={{ gap: 8 }}>
              <button className="btn-rf-o" disabled={busy === f._id} onClick={() => runNow(f)}>{busy === f._id ? <Spinner animation="border" size="sm" /> : "Run now"}</button>
              <button className="btn-rf-o" disabled={busy === f._id} onClick={() => toggle(f)}>{f.status === "active" ? "Pause" : "Resume"}</button>
              <button className="btn-rf-o" onClick={() => setEditor({ show: true, flow: f })}>Edit</button>
              <button className="btn-rf-danger" onClick={() => remove(f)}>Delete</button>
            </div>
          </div>
        );
      })}

      <FlowEditor show={editor.show} flow={editor.flow} channels={channels} ctx={ctx} onClose={() => setEditor({ show: false, flow: null })} onSaved={load} />
    </div>
  );
};

export default FlowsPage;
